import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Browser } from "@capacitor/browser";
import { Platform } from "@ionic/angular";
import { BehaviorSubject, Observable, Subject, catchError, of } from "rxjs";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./localStorage.service";
import { TranslateService } from "@ngx-translate/core";
import { BobToastService } from "src/app/shared/toast";
import type { BobToastColor } from "src/app/shared/toast";
import {
  ModalQueuePriority,
  ModalQueueService,
} from "./modal-queue.service";
import { ModalOverlayCoordinationService } from "./modal-overlay-coordination.service";
import { LocationDisclosureComponent } from "../shared/modals/location-disclosure/location-disclosure.component";
// import { Platform } from '@angular/cdk/platform';

export type LocationDisclosureChoice = "accepted" | "declined";
export type LocationActivationMode = "gps" | "manual" | "both";

const LOCATION_DISCLOSURE_ACCEPTED_KEY = "location_disclosure_accepted";
const LOCATION_DISCLOSURE_DECLINED_KEY = "location_disclosure_declined";

@Injectable({
  providedIn: "root",
})
export class AppService {
  url = `${environment.apiUrl}`;
  authUrl = `${environment.authUrl}`;
  public _splashViewedSubject = new BehaviorSubject<any>(
    localStorage.getItem("splash_viewed")
      ? localStorage.getItem("splash_viewed")
      : 0
  );

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    public platform: Platform,
    private bobToast: BobToastService,
    private translateService: TranslateService,
    private modalQueue: ModalQueueService,
    private modalOverlayCoordination: ModalOverlayCoordinationService
  ) { }
  getPlatform(): string {
    if (this.platform.is("ios")) {
      return "ios";
    } else if (this.platform.is("android")) {
      return "android";
    } else {
      return "unknown";
    }
  }

  isMobileDevice(): boolean {
    return this.platform.is("ios") || this.platform.is("android");
  }

  /** Legacy alias — BobToast card above footer (bottom). */
  async presentToast(message: string, color: string = "danger") {
    const semanticColor = this.mapLegacyToastColor(color);
    await this.bobToast.showBottom(message, semanticColor, { duration: 3000 });
  }

  private mapLegacyToastColor(color: string): BobToastColor {
    switch (color) {
      case "success":
        return "success";
      case "warning":
        return "warning";
      case "medium":
        return "medium";
      case "danger":
      default:
        return "danger";
    }
  }

  /**
   *
   *
   *  modals states
   *
   *
   */
  // stores declaration
  private openCancelModal: BehaviorSubject<any> = new BehaviorSubject<{
    open: boolean;
    id?: number;
    type?: string;
  }>({
    open: false,
  });
  private userCompletion: BehaviorSubject<number> = new BehaviorSubject<number>(
    localStorage.getItem("completion")
      ? Number(localStorage.getItem("completion"))
      : 0
  );
  private openLocationModal: BehaviorSubject<any> =
    new BehaviorSubject<boolean>(false);
  private locationActivationMode = new BehaviorSubject<LocationActivationMode>(
    this.resolveLocationActivationMode()
  );
  private locationDisclosureResolved = new BehaviorSubject<boolean>(
    !this.isProviderLocationDisclosureApplicable() ||
      this.getLocationDisclosureChoice() !== null
  );
  private unsupportedLocationModal: BehaviorSubject<any> =
    new BehaviorSubject<boolean>(false);
  private workFinishedModal: BehaviorSubject<any> =
    new BehaviorSubject<boolean>(false);

  private cleaningSignatuerPad: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private canceledDevisModal: BehaviorSubject<{
    open: boolean;
    id: number | null;
  }> = new BehaviorSubject<{ open: boolean; id: number | null }>({
    open: false,
    id: 0,
  });

  private homeData: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private demandeToCreate: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private transporterDate: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  private checkPaymentCartModal: Subject<boolean> = new Subject<boolean>();
  private paymentWithdrawModalSubject: Subject<boolean> =
    new Subject<boolean>();
  private freeDemandModalSubject: Subject<boolean> = new Subject<boolean>();
  private bankLinkModalSubject: Subject<boolean> = new Subject<boolean>();
  private backgroundLocationAlwaysModalSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private permissionGuideModalOpenSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  private permissionGuideLastClosedAt = 0;
  private readonly permissionGuideOverlayCooldownMs = 600;

  private readonly BG_LOCATION_ALWAYS_DISMISSED_KEY = 'bg_location_always_dismissed_at';
  private readonly BG_LOCATION_ALWAYS_COOLDOWN_MS = 60 * 60 * 1000; // 1 hour
  private devisPrix: Subject<boolean> = new Subject<boolean>();
  private nativeRetractionFeedbackSubject: Subject<{
    data?: Record<string, any>;
  }> = new Subject<{ data?: Record<string, any> }>();
  private recentRetractionFeedbackKeys = new Map<string, number>();

  private freeDemandClientModalSubject: Subject<boolean> =
    new Subject<boolean>();
  // dispatchers
  dispatchModal(modal: string, value: any) {
    switch (modal) {
      case "CANCEL_DEMAND_MODAL":
        this.openCancelModal.next(value);
        break;
      case "DETECT_LOCATION_MODAL":
        if (value === true && !this.canShowLocationModals()) {
          break;
        }
        if (value === true) {
          this.locationActivationMode.next(this.resolveLocationActivationMode());
        }
        this.modalOverlayCoordination.setLocationActivationModalOpen(!!value);
        this.openLocationModal.next(value);
        break;
      case "UNSUPPORTED_LOCATION_MODAL":
        this.modalOverlayCoordination.setOverlayOpen("unsupported-location", !!value);
        this.unsupportedLocationModal.next(value);
        break;
      case "CLEAR_PAD":
        this.cleaningSignatuerPad.next(value);
        break;
      case "CANCELED_DEVIS_MODAL":
        this.canceledDevisModal.next(value);
        break;
      case "CHECK_PAYMENT_MODAL":
        this.checkPaymentCartModal.next(value);
        break;
      case "WITHDRAW_MODAL":
        this.paymentWithdrawModalSubject.next(value);
        break;
      case "FREE_DEMAND_MODAL":
        this.freeDemandModalSubject.next(value);
        break;
      case "BANK_LINK_MODAL":
        this.bankLinkModalSubject.next(value);
        break;
      case "BACKGROUND_LOCATION_ALWAYS_MODAL":
        if (value === true && this.backgroundLocationAlwaysModalSubject.value === true) {
          break;
        }
        this.backgroundLocationAlwaysModalSubject.next(value);
        break;
      case "FREE_DEMAND_CLIENT_MODAL":
        this.freeDemandClientModalSubject.next(value);
        break;
      case "CHECK_DEVIS_PRIX":
        this.devisPrix.next(value);
        break;
      case "WORK_FINISHED_MODAL":
        this.workFinishedModal.next(value);
        break;
      default:
        break;
    }
  }

  /** Reset footer/template modal flags before external navigation (push, deep link). */
  resetTransientModalState(): void {
    this.dispatchModal("CANCEL_DEMAND_MODAL", { open: false, id: null });
    this.dispatchModal("DETECT_LOCATION_MODAL", false);
    this.dispatchModal("UNSUPPORTED_LOCATION_MODAL", false);
    this.dispatchModal("CHECK_PAYMENT_MODAL", false);
    this.dispatchModal("WITHDRAW_MODAL", false);
    this.dispatchModal("FREE_DEMAND_MODAL", false);
    this.dispatchModal("BANK_LINK_MODAL", false);
    this.dispatchModal("BACKGROUND_LOCATION_ALWAYS_MODAL", false);
    this.dispatchModal("FREE_DEMAND_CLIENT_MODAL", false);
    this.dispatchModal("CHECK_DEVIS_PRIX", false);
    this.dispatchModal("WORK_FINISHED_MODAL", false);
    this.setPermissionGuideModalOpen(false);
  }

  dispatchdata(fn: string, value: any) {
    switch (fn) {
      case "DEMANDE_TO_CREATE":
        this.demandeToCreate.next(value);
        break;
      case "HOME_DATA":
        this.homeData.next(value);
        break;
      case "TRANSPORTER_DATA":
        const oldValue = this.transporterDate.value;
        oldValue
          ? this.transporterDate.next({
            ...oldValue,
            ...value,
          })
          : this.transporterDate.next(value);
        break;
      default:
        break;
    }
  }
  setUserCompletion(value: number) {
    this.userCompletion.next(value);
    localStorage.setItem("completion", String(value));
  }

  cleaningSignatuerPadObservable() {
    return this.cleaningSignatuerPad.asObservable();
  }
  cancelModalObservable() {
    return this.openCancelModal.asObservable();
  }
  locationModalObservable() {
    return this.openLocationModal.asObservable();
  }
  locationActivationModeObservable(): Observable<LocationActivationMode> {
    return this.locationActivationMode.asObservable();
  }
  locationDisclosureResolvedObservable(): Observable<boolean> {
    return this.locationDisclosureResolved.asObservable();
  }
  getLocationDisclosureChoice(): LocationDisclosureChoice | null {
    if (localStorage.getItem(LOCATION_DISCLOSURE_ACCEPTED_KEY) === "true") {
      return "accepted";
    }
    if (localStorage.getItem(LOCATION_DISCLOSURE_DECLINED_KEY) === "true") {
      return "declined";
    }
    return null;
  }
  isProviderLocationDisclosureApplicable(): boolean {
    if (!this.canShowLocationModals()) {
      return false;
    }
    return this.localStorageService.getUserRole() === "prestataire";
  }
  canShowLocationModals(): boolean {
    return this.localStorageService.isAuth();
  }
  isLocationDisclosurePending(): boolean {
    if (!this.isProviderLocationDisclosureApplicable()) {
      return false;
    }
    return this.getLocationDisclosureChoice() === null;
  }
  async presentProviderLocationDisclosureIfNeeded(): Promise<LocationDisclosureChoice | null> {
    if (!this.canShowLocationModals()) {
      return null;
    }
    if (!this.isProviderLocationDisclosureApplicable()) {
      return null;
    }

    const existingChoice = this.getLocationDisclosureChoice();
    if (existingChoice) {
      return existingChoice;
    }

    const dismissed = await this.modalQueue.enqueueModal<unknown>(
      {
        component: LocationDisclosureComponent,
        backdropDismiss: false,
        cssClass: "location-disclosure-modal",
      },
      {
        id: "location-disclosure",
        priority: ModalQueuePriority.HIGH,
      }
    );

    if (dismissed?.role === "accept") {
      this.saveLocationDisclosureChoice("accepted");
      return "accepted";
    }
    if (dismissed?.role === "decline") {
      this.saveLocationDisclosureChoice("declined");
      return "declined";
    }
    return null;
  }
  saveLocationDisclosureChoice(choice: LocationDisclosureChoice): void {
    if (choice === "accepted") {
      localStorage.setItem(LOCATION_DISCLOSURE_ACCEPTED_KEY, "true");
      localStorage.removeItem(LOCATION_DISCLOSURE_DECLINED_KEY);
    } else {
      localStorage.setItem(LOCATION_DISCLOSURE_DECLINED_KEY, "true");
      localStorage.removeItem(LOCATION_DISCLOSURE_ACCEPTED_KEY);
    }
    this.locationDisclosureResolved.next(true);
    this.locationActivationMode.next(this.resolveLocationActivationMode());
  }
  resolveLocationActivationMode(): LocationActivationMode {
    if (!this.isProviderLocationDisclosureApplicable()) {
      return "both";
    }
    return this.getLocationDisclosureChoice() === "declined" ? "manual" : "gps";
  }
  isLocationActivationModalOpen(): boolean {
    return this.modalOverlayCoordination.isLocationActivationModalOpen();
  }
  async waitForModalQueueIdle(maxWaitMs = 120_000): Promise<void> {
    const started = Date.now();
    while (this.modalQueue.hasPendingJobs() && Date.now() - started < maxWaitMs) {
      await this.delay(150);
    }
  }
  async waitForLocationOnboardingToSettle(maxWaitMs = 120_000): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
      if (
        !this.isLocationDisclosurePending() &&
        !this.isLocationActivationModalOpen() &&
        !this.modalQueue.hasPendingJobs()
      ) {
        return;
      }
      await this.delay(200);
    }
  }
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
  unsupportedLocationModalObservable() {
    return this.unsupportedLocationModal.asObservable();
  }

  userCompletionObservable() {
    return this.userCompletion.asObservable();
  }
  canceledDevisModalObservable() {
    return this.canceledDevisModal.asObservable();
  }
  checkModalObservable() {
    return this.checkPaymentCartModal.asObservable();
  }
  createDemandeDataObservable() {
    return this.demandeToCreate.asObservable();
  }
  transporterDateObservable() {
    return this.transporterDate.asObservable();
  }
  getTransporterDate() {
    return this.transporterDate.value;
  }

  withdrawObservable() {
    return this.paymentWithdrawModalSubject.asObservable();
  }
  freeDemandModalObservable() {
    return this.freeDemandModalSubject.asObservable();
  }
  bankLinkModalObservable() {
    return this.bankLinkModalSubject.asObservable();
  }
  backgroundLocationAlwaysModalObservable() {
    return this.backgroundLocationAlwaysModalSubject.asObservable();
  }
  permissionGuideModalOpenObservable() {
    return this.permissionGuideModalOpenSubject.asObservable();
  }
  notifyDemandRetractionFeedback(data?: Record<string, any>) {
    const aliases = this.resolveDemandRetractionAliases(data);
    if (this.hasRecentRetractionAlias(aliases)) {
      return;
    }

    const now = Date.now();
    aliases.forEach((alias) => this.recentRetractionFeedbackKeys.set(alias, now));
    this.cleanupRetractionFeedbackAliases(now);
    this.localStorageService.dropCurrentDemand();
    this.presentToast(
      this.translateService.instant("PROVIDER.DAMANDE.DEMAND_RETRACTED"),
      "danger"
    );
    this.nativeRetractionFeedbackSubject.next({ data });
  }
  nativeRetractionFeedbackObservable() {
    return this.nativeRetractionFeedbackSubject.asObservable();
  }
  private resolveDemandRetractionAliases(data?: Record<string, any>): string[] {
    const demande = data?.["demande"];
    const rawAliases = [
      data?.["event_id"],
      data?.["demande_id"],
      data?.["demand_id"],
      data?.["id"],
      demande && typeof demande === "object"
        ? (demande as Record<string, any>)["id"]
        : demande,
    ];
    const aliases = rawAliases
      .filter((value) => value !== null && value !== undefined && value !== "")
      .map((value) => String(value));

    return aliases.length ? Array.from(new Set(aliases)) : [`local-${Date.now()}`];
  }
  private hasRecentRetractionAlias(aliases: string[]): boolean {
    const now = Date.now();
    return aliases.some((alias) => {
      const seenAt = this.recentRetractionFeedbackKeys.get(alias);
      return !!seenAt && now - seenAt < 5000;
    });
  }
  private cleanupRetractionFeedbackAliases(now: number) {
    for (const [alias, timestamp] of this.recentRetractionFeedbackKeys) {
      if (now - timestamp > 30000) {
        this.recentRetractionFeedbackKeys.delete(alias);
      }
    }
  }
  setPermissionGuideModalOpen(value: boolean) {
    if (!value) {
      this.permissionGuideLastClosedAt = Date.now();
    }
    this.permissionGuideModalOpenSubject.next(value);
  }
  canShowPermissionRelatedOverlay(): boolean {
    if (this.modalQueue.isPresenting() || this.modalQueue.hasPendingJobs()) {
      return false;
    }
    if (this.permissionGuideModalOpenSubject.value) {
      return false;
    }
    return Date.now() - this.permissionGuideLastClosedAt >= this.permissionGuideOverlayCooldownMs;
  }
  markBackgroundLocationAlwaysDismissed(): void {
    try {
      localStorage.setItem(this.BG_LOCATION_ALWAYS_DISMISSED_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    this.dispatchModal('BACKGROUND_LOCATION_ALWAYS_MODAL', false);
  }

  canShowBackgroundLocationAlwaysModal(): boolean {
    if (!this.canShowPermissionRelatedOverlay()) return false;
    try {
      const raw = localStorage.getItem(this.BG_LOCATION_ALWAYS_DISMISSED_KEY);
      if (raw) {
        const last = Number(raw);
        if (!isNaN(last) && Date.now() - last < this.BG_LOCATION_ALWAYS_COOLDOWN_MS) {
          return false;
        }
      }
    } catch {
      // ignore
    }
    return true;
  }
  freeDemandClientModalObservable() {
    return this.freeDemandClientModalSubject.asObservable();
  }
  devisPrixModalObservable() {
    return this.devisPrix.asObservable();
  }
  workFinishedModalObservable() {
    return this.workFinishedModal.asObservable();
  }

  gethomeData() {
    return this.homeData.value;
  }
  homeDataObservable() {
    return this.homeData.asObservable();
  }
  /**
   *
   *
   *  app api requests
   *
   *
   */
  getBuildVersion() {
    return this.http.get("../../assets/settings/version.json");
  }

  async openUrl(url: string) {
    await Browser.open({ url: url });
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      console.error(`${operation} failed: ${error.message}`);
      return of(result as T);
    };
  }

  /*==============================
  Domaine list
  ================================*/
  getDomain() {
    return this.http
      .get(`${this.url}/frontoffice/domaines`, { observe: "response" })
      .pipe(catchError(this.handleError<HttpResponse<any>>("domaine")));
  }

  /*==============================
  get Home Data Stats 
  ================================*/

  refreshHomeDataStats() {
    // const auth = this.localStorageService.getToken();
    // if (auth) {
      this.getHomeDataStats().subscribe((response: any) => {
        if (response && response.message === "success") {
          this.homeData.next(response);
        }
      });
    // }
  }
  getHomeDataStats() {
    const payload = {
      status: "",
      opposite: ["FINISHED", "REFUSED", "CANCELED"],
    };
    return this.http.post(
      `${this.authUrl}/frontoffice/getHomeDataStats`,
      payload
    );
  }

  /**
   * Page carte pros : filtrage domaine + pagination prestataires (villes / totaux sur ensemble filtré).
   * Voir `backend_retours.md` — ne pas utiliser `getHomeDataStats` pour le filtre métier.
   */
  getCarteProsSummary(params?: {
    domaine_key?: string | null;
    page?: number;
    per_page?: number;
  }) {
    const from: Record<string, string | number> = {};
    const key = params?.domaine_key?.trim();
    if (key) {
      from["domaine_key"] = key;
    }
    if (params?.page != null) {
      from["page"] = params.page;
    }
    if (params?.per_page != null) {
      from["per_page"] = params.per_page;
    }
    const query = new HttpParams({ fromObject: from as any });
    return this.http.get(`${this.authUrl}/frontoffice/carte-pros/summary`, {
      params: query,
    });
  }

  getLiveActivity() {
    return this.http.get(`${this.url}/frontoffice/live-activity`);
  }

  getTopEvolutions(params: { limit?: number; finished_demande?: boolean } = {}) {
    const query = new HttpParams({ fromObject: params as any });
    return this.http.get(`${this.url}/frontoffice/evolutions/top`, { params: query });
  }

  getEvolutions(params: {
    page?: number;
    per_page?: number;
    sort?: "recent" | "rating_desc" | "rating_asc";
    rating?: number;
    with_photos?: boolean;
    domaine_key?: string;
    finished_demande?: boolean;
  } = {}) {
    const query = new HttpParams({ fromObject: params as any });
    return this.http.get(`${this.url}/frontoffice/evolutions`, { params: query });
  }

  /** Alias backend de `getEvolutions` — `GET .../frontoffice/avis` (même query / réponse). */
  getAvis(
    params: {
      page?: number;
      per_page?: number;
      sort?: "recent" | "rating_desc" | "rating_asc";
      rating?: number;
      with_photos?: boolean;
      domaine_key?: string;
      finished_demande?: boolean;
    } = {}
  ) {
    const query = new HttpParams({ fromObject: params as any });
    return this.http.get(`${this.url}/frontoffice/avis`, { params: query });
  }

  /*==============================
  speciality list By Id
  ================================*/
  getSpecialityById(id: number) {
    return this.http.get(`${this.url}/frontoffice/domaines/${id}/specialities`);
  }

  /*==============================
  speciality list By Id
  ================================*/
  getSpecialitysByIds(formData: any) {
    return this.http
      .post(`${this.url}/frontoffice/domaines/specialities`, formData, {
        observe: "response",
      })
      .pipe(catchError(this.handleError<HttpResponse<any>>("domaine")));
  }
  /*==============================
  speciality list By Idcreate demand
  ================================*/
  createDemand(formData: any) {
    return this.http.post(`${this.authUrl}/frontoffice/demande/new`, formData, {
      observe: "response",
    });
  }

  /*==============================
  get user By Id
  ================================*/
  getUserById(id: number | string) {
    return this.http.get(`${this.authUrl}/backoffice/users/${id}`, {
      observe: "response",
    });
  }
  setSpashViewed(data: any) {
    localStorage.setItem("splash_viewed", data);
    this._splashViewedSubject.next(data);
  }
  getSplashViewed(): Observable<any> {
    return this._splashViewedSubject.asObservable();
  }

  getMapUrl() {
    return this.http.get(`${this.url}/get_map_url`);
  }
}
